import { useState, useEffect } from 'react';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9orh3gsenbnioleskhg';

export default function AboutPage() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: '韩漫屋上的韩漫都是免费的吗？',
      a: '是的！韩漫屋上所有韩国漫画均可免费在线阅读，无需注册、无需付费。我们采用下拉式阅读模式，让您享受流畅的漫画阅读体验。',
    },
    {
      q: '韩漫屋支持哪些阅读模式？',
      a: '韩漫屋主打下拉式阅读模式，您只需向下滚动即可连续阅读。同时也支持目录跳转，方便您快速定位到感兴趣的章节。所有漫画均为高清画质呈现。',
    },
    {
      q: '韩漫屋的内容更新频率如何？',
      a: '韩漫屋每日更新热门韩国漫画，确保您能第一时间阅读到最新的韩漫章节。我们与多家韩漫平台保持同步更新，让您不错过任何精彩内容。',
    },
    {
      q: '韩漫屋有哪些漫画分类？',
      a: '韩漫屋收录了恋爱、奇幻、动作、剧情、悬疑、搞笑、恐怖、日常等多种分类的韩国漫画。您可以通过分类页面快速找到感兴趣的漫画类型。',
    },
    {
      q: '如何在韩漫屋搜索想看的漫画？',
      a: '您可以通过顶部导航栏的搜索功能，或进入分类页面使用搜索框，输入漫画名称、作者名或标签关键词即可快速找到目标韩漫。',
    },
    {
      q: '韩漫屋是否支持手机阅读？',
      a: '当然！韩漫屋采用响应式设计，完美适配手机、平板和电脑等多种设备。下拉式阅读模式尤其适合手机端使用，带给您最佳的移动阅读体验。',
    },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '韩漫屋—免费韩国漫画在线阅读平台，下拉式观看模式，高清画质大全，每日更新热门韩漫作品。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hanmanwu.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <>
      <SEO
        title="关于我们_韩漫屋_免费韩国漫画在线阅读平台"
        description="韩漫屋致力于为中文读者提供免费韩国漫画在线阅读服务。下拉式观看模式，高清画质大全，每日更新热门韩漫。了解韩漫屋的品牌故事与联系方式。"
        keywords="韩漫屋,韩国漫画,在线阅读,关于我们,免费韩漫,下拉式韩漫"
        canonicalUrl="/about"
        ogType="website"
        schema={{ ...orgSchema, ...faqSchema }}
      />

      <div className="pt-20 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6 mb-12 md:mb-16">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                关于韩漫屋
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                韩漫屋是一个专注于韩国漫画免费在线阅读的中文平台。我们致力于为中文读者提供最优质的韩漫阅读体验，
                采用下拉式阅读模式，搭配高清画质，让您随时随地畅享韩国漫画的精彩世界。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-3xl md:text-4xl font-bold text-primary-500">500+</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">收录韩漫</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-3xl md:text-4xl font-bold text-accent-500">99%</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">高清画质</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-3xl md:text-4xl font-bold text-secondary-500">日更</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">每日更新</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-3xl md:text-4xl font-bold text-primary-500">100万+</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">月活读者</p>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-download-cloud-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">下拉式阅读</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">流畅的下拉式阅读体验，无需翻页，一滑到底。特别适合手机端使用。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className="ri-hd-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">高清画质</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">所有漫画均为高清画质呈现，细致入微的画面让您沉浸式享受每一部韩漫作品。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                  <i className="ri-refresh-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">每日更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">与多家韩漫平台同步更新，确保读者第一时间看到最新韩漫章节内容。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-price-tag-3-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">完全免费</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">无需注册，无需付费，所有韩国漫画均可免费在线阅读。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                  <i className="ri-smartphone-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">多端适配</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">完美适配手机、平板、电脑等设备，随时随地畅读韩漫。</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                  <i className="ri-layout-grid-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">丰富分类</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">涵盖恋爱、奇幻、动作、悬疑等多种分类，满足不同读者的阅读偏好。</p>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <i className={`text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                      activeFaq === idx ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                    }`}></i>
                  </button>
                  {activeFaq === idx && (
                    <div className="px-5 pb-4 pt-0">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8">
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500">contact@hanmanwu.com</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                  <i className="ri-customer-service-2-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">工作日 9:00-18:00</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                  <i className="ri-question-answer-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">反馈建议</h3>
                <p className="text-xs text-foreground-500">欢迎提供宝贵意见</p>
              </div>
            </div>

            <div className="rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-800 mb-4">发送消息</h3>
              <form
                id="about-contact-form"
                data-readdy-form
                action={FORM_SUBMIT_URL}
                method="POST"
                encType="application/x-www-form-urlencoded"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    const honeypot = formData.get('phone_alt') as string;
                    if (honeypot && honeypot.trim() !== '') {
                      const msgEl = document.getElementById('contact-form-message');
                      if (msgEl) {
                        msgEl.textContent = '消息已发送，我们会尽快回复！';
                        msgEl.className = 'text-sm text-green-600 mt-4 text-center';
                      }
                      return;
                    }
                    formData.delete('phone_alt');
                    const urlEncoded = new URLSearchParams();
                    formData.forEach((value, key) => {
                      urlEncoded.append(key, value as string);
                    });
                    try {
                      const res = await fetch(FORM_SUBMIT_URL, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: urlEncoded.toString(),
                      });
                      const responseText = await res.text();
                      let serverMsg = '';
                      try {
                        const parsed = JSON.parse(responseText);
                        serverMsg = parsed?.meta?.message || parsed?.message || '';
                      } catch {
                        serverMsg = responseText;
                      }
                      const isSpam = serverMsg.toLowerCase().includes('spam');
                      const msgEl = document.getElementById('contact-form-message');
                      if (res.ok && !isSpam) {
                        if (msgEl) {
                          msgEl.textContent = '感谢您的消息！我们会尽快回复。';
                          msgEl.className = 'text-sm text-green-600 mt-4 text-center';
                        }
                        form.reset();
                      } else {
                        if (msgEl) {
                          msgEl.textContent = serverMsg || '发送失败，请稍后再试。';
                          msgEl.className = 'text-sm text-red-500 mt-4 text-center';
                        }
                      }
                    } catch {
                      const msgEl = document.getElementById('contact-form-message');
                      if (msgEl) {
                        msgEl.textContent = '网络错误，请稍后再试。';
                        msgEl.className = 'text-sm text-red-500 mt-4 text-center';
                      }
                    }
                  }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                  <div id="contact-form-message"></div>
                </form>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}